import { LitElement, html, css, nothing } from "lit";
import { property } from "lit/decorators.js";
import "@/components/renderers/layout/divider";

export class SbPagination extends LitElement {
  @property({ type: Number }) totalPages: number = 1;
  @property({ type: Number }) currentPage: number = 1;

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }
  // Compute the visible page numbers (max 3)
  private get visiblePages(): number[] {
    if (this.totalPages <= 0) return [];
    if (this.totalPages <= 3) {
      return Array.from({ length: this.totalPages }, (_, i) => i + 1);
    }
    let start = Math.max(1, this.currentPage - 1);
    if (start + 2 > this.totalPages) {
      start = this.totalPages - 2;
    }
    return [start, start + 1, start + 2];
  }

  // Emit page-change event
  private goTo(page: number) {
    if (page === this.currentPage || page < 1 || page > this.totalPages) return;
    this.dispatchEvent(
      new CustomEvent("page-change", {
        detail: { page },
        bubbles: true,
        composed: true,
      }),
    );
  }

  render() {
    // Hide pagination if only one page or no pages
    if (this.totalPages <= 1) return nothing;

    const isFirst = this.currentPage === 1;
    const isLast = this.currentPage === this.totalPages;

    return html`
      <nav class="sb-flex sb-items-center sb-gap-1" aria-label="Pagination">
        <!-- First page -->
        <rtg-button
          variant="link"
          size="icon"
          ?disabled=${isFirst}
          @click=${() => this.goTo(1)}
          aria-label="First page"
        >
          <span aria-hidden="true">&laquo;</span>
        </rtg-button>

        <!-- Previous page -->
        <rtg-button
          variant="link"
          size="icon"
          ?disabled=${isFirst}
          @click=${() => this.goTo(this.currentPage - 1)}
          aria-label="Previous page"
        >
          <span aria-hidden="true">&lsaquo;</span>
        </rtg-button>

        <!-- Page numbers (max 3) -->
        ${this.visiblePages.map(
          (page) => html`
            <rtg-button
              variant=${page === this.currentPage ? "secondary" : "link"}
              size="sm"
              @click=${() => this.goTo(page)}
              aria-current=${page === this.currentPage ? "page" : nothing}
            >
              ${page}
            </rtg-button>
          `,
        )}

        <!-- Next page -->
        <rtg-button
          variant="link"
          size="icon"
          ?disabled=${isLast}
          @click=${() => this.goTo(this.currentPage + 1)}
          aria-label="Next page"
        >
          <span aria-hidden="true">&rsaquo;</span>
        </rtg-button>

        <!-- Last page -->
        <rtg-button
          variant="link"
          size="icon"
          ?disabled=${isLast}
          @click=${() => this.goTo(this.totalPages)}
          aria-label="Last page"
        >
          <span aria-hidden="true">&raquo;</span>
        </rtg-button>
        <rtg-separator orientation="vertical"></rtg-separator>
        <div class="sb-ml-2"></div>

        <div class="sb-flex sb-items-center sb-gap-1">
          <div class="sb-flex sb-gap-1">
            <rtg-typography-muted>Go ${" "} </rtg-typography-muted>
            <rtg-typography-muted>to ${" "} </rtg-typography-muted>
          </div>
          <rtg-input
            .value=${this.currentPage.toString()}
            @change=${(e: Event) => {
              this.goTo(parseInt((e.target as HTMLInputElement).value));
            }}
          ></rtg-input>

          <rtg-typography-muted>page</rtg-typography-muted>
        </div>
      </nav>
    `;
  }

  static styles = css`
    :host {
      display: block;
    }
  `;
}

customElements.define("sb-pagination", SbPagination);

declare global {
  interface HTMLElementTagNameMap {
    "sb-pagination": SbPagination;
  }
}
